// AIPage — Full-page AI chat experience.
// Pure client mock — NEVER calls /api/v1/agent/search.
// TODO(ai-API-PR): Wire to real SSE stream at /api/v1/agent/search

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Sparkles, Mic, Send, ChevronLeft } from 'lucide-react';
import { AIBubble, AITypingBubble } from '@/components/AIBubble';
import { AIPlanCard } from '@/components/AIPlanCard';
import { notYetWiredToast } from '@/components/primitives';
import { AI_SUGGESTIONS, CHAT_HISTORY, getAIResponse } from '@/lib/ai-mock';
import type { AIPlan } from '@/lib/ai-mock';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', accent: '#FFB800', soft: '#FFE3D2', chipInk: '#B5391C',
  line: 'rgba(26,15,8,0.08)', lineStr: '#E8E0D8',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
  display: '"Fraunces Variable","Fraunces",Georgia,serif',
};

interface Message {
  id: string;
  from: 'ai' | 'me';
  text: string;
  plan?: AIPlan;
}

const INITIAL_MESSAGES: Message[] = [
  {
    id: 'init',
    from: 'ai',
    text: "hey there. i'm Global Flyt AI. tell me a vibe, a budget, or a destination — and i'll cobble together a real itinerary.",
  },
];

export function AIPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [replyCount, setReplyCount] = useState(0);
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typing]);

  const send = useCallback((text?: string) => {
    const m = (text ?? input).trim();
    if (!m) return;
    setInput('');

    const userMsg: Message = { id: `u-${Date.now()}`, from: 'me', text: m };
    setMessages(prev => [...prev, userMsg]);
    setTyping(true);

    // optimistic + delayed AI reply
    setTimeout(() => {
      setTyping(false);
      const { text: aiText, plan } = getAIResponse(replyCount);
      const aiMsg: Message = {
        id: `a-${Date.now()}`,
        from: 'ai',
        text: aiText,
        plan,
      };
      setMessages(prev => [...prev, aiMsg]);
      setReplyCount(c => c + 1);
    }, 2500);
  }, [input, replyCount]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const showSuggestions = messages.length === 1 && !typing;

  return (
    <div style={{ background: T.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, display: 'flex', maxWidth: 1280, width: '100%', margin: '0 auto', padding: '0 clamp(16px, 4vw, 32px) 32px' }}>

        {/* ── Left sidebar 240px ── */}
        <aside style={{
          width: 240, flexShrink: 0, paddingTop: 24, paddingRight: 24,
          borderRight: `1px solid ${T.lineStr}`,
          display: 'flex', flexDirection: 'column', gap: 0,
        }}>
          {/* Back to home */}
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'none', border: 'none', cursor: 'pointer', padding: '0 0 20px',
            }}
          >
            <ChevronLeft size={14} stroke={T.inkDim} strokeWidth={1.8}/>
            <span style={{ fontFamily: T.sans, fontSize: 12, color: T.inkDim }}>Back</span>
          </button>

          {/* New chat button */}
          <button
            onClick={() => { setMessages(INITIAL_MESSAGES); setReplyCount(0); setActiveChat(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              width: '100%', height: 44, borderRadius: 999, border: 'none',
              background: T.ink, color: '#FFF5EE',
              fontFamily: T.sans, fontSize: 13, fontWeight: 600,
              cursor: 'pointer', marginBottom: 20,
              transition: 'opacity 200ms ease',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '0.85'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.opacity = '1'; }}
          >
            <Plus size={16} stroke="#FFF5EE" strokeWidth={2}/>
            new chat
          </button>

          {/* Section label */}
          <div style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim, letterSpacing: 1.5, fontWeight: 600, textTransform: 'uppercase', marginBottom: 10 }}>
            Recent
          </div>

          {/* Chat history */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {CHAT_HISTORY.map(c => (
              <button
                key={c.id}
                onClick={() => setActiveChat(c.id)}
                style={{
                  padding: '10px 12px', borderRadius: 14, border: 'none', cursor: 'pointer',
                  background: activeChat === c.id ? T.soft : 'transparent',
                  textAlign: 'left', transition: 'background 150ms ease',
                }}
                onMouseEnter={e => {
                  if (activeChat !== c.id) (e.currentTarget as HTMLButtonElement).style.background = T.line;
                }}
                onMouseLeave={e => {
                  if (activeChat !== c.id) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                }}
              >
                <div style={{ fontFamily: T.sans, fontSize: 12, fontWeight: 600, color: activeChat === c.id ? T.chipInk : T.ink, marginBottom: 2 }}>{c.title}</div>
                <div style={{ fontFamily: T.sans, fontSize: 10.5, color: T.inkDim, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.preview}</div>
                <div style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim, marginTop: 2 }}>{c.timestamp}</div>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Main chat pane ── */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: 24, paddingLeft: 40, minWidth: 0 }}>
          {/* header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{
              width: 42, height: 42, borderRadius: 14,
              background: `linear-gradient(135deg, ${T.primary}, ${T.accent})`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Sparkles size={20} stroke="#fff" strokeWidth={1.8}/>
            </div>
            <div>
              <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 500, color: T.ink, lineHeight: 1 }}>Global Flyt AI</div>
              <div style={{ fontFamily: T.sans, fontSize: 11, color: '#1F8A5B', fontWeight: 500, marginTop: 3 }}>● online · plans in seconds</div>
            </div>
          </div>

          {/* messages scroll area */}
          <div
            ref={scrollRef}
            style={{
              flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16,
              paddingRight: 8, paddingBottom: 16,
              minHeight: 0,
              maxHeight: 'calc(100vh - 340px)',
            }}
          >
            {messages.map(m => (
              <div key={m.id} className="animate-fade-up">
                <AIBubble variant={m.from === 'me' ? 'user' : 'ai'} text={m.text}>
                  {m.plan && <AIPlanCard plan={m.plan}/>}
                </AIBubble>
              </div>
            ))}
            {typing && <AITypingBubble/>}
          </div>

          {/* suggestion strip */}
          {showSuggestions && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16,
            }}>
              {AI_SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  style={{
                    padding: '10px 14px', borderRadius: 16, flexShrink: 0,
                    background: T.surface, border: `1px solid ${T.lineStr}`,
                    fontFamily: T.sans, fontSize: 12.5, color: T.ink,
                    maxWidth: 260, textAlign: 'left', cursor: 'pointer',
                    transition: 'border-color 150ms ease',
                  }}
                  onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.primary; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.borderColor = T.lineStr; }}
                >{s}</button>
              ))}
            </div>
          )}

          {/* input row */}
          <div style={{
            display: 'flex', gap: 10, alignItems: 'center',
            background: T.surface, borderRadius: 999,
            border: `1.5px solid ${T.lineStr}`,
            padding: '6px 6px 6px 20px',
            boxShadow: '0 8px 24px -8px rgba(0,0,0,0.08)',
          }}>
            {/* mic */}
            <button
              onClick={() => notYetWiredToast('Voice input')}
              style={{
                width: 36, height: 36, borderRadius: '50%',
                background: T.soft, border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', flexShrink: 0,
              }}
            >
              <Mic size={16} stroke={T.chipInk} strokeWidth={1.8}/>
            </button>

            {/* text input */}
            <input
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="plan a trip…"
              disabled={typing}
              style={{
                flex: 1, border: 'none', outline: 'none', background: 'transparent',
                fontFamily: T.sans, fontSize: 14, color: T.ink,
                caretColor: T.primary,
              }}
            />

            {/* send */}
            <button
              onClick={() => send()}
              disabled={!input.trim() || typing}
              style={{
                width: 40, height: 40, borderRadius: '50%', border: 'none',
                background: input.trim() && !typing ? T.primary : T.lineStr,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: input.trim() && !typing ? 'pointer' : 'not-allowed',
                transition: 'background 200ms ease',
                flexShrink: 0,
              }}
            >
              <Send size={15} stroke={input.trim() && !typing ? '#fff' : T.inkDim} strokeWidth={1.8}/>
            </button>
          </div>

          <div style={{ textAlign: 'center', marginTop: 8, fontFamily: T.sans, fontSize: 11, color: T.inkDim }}>
            AI suggestions are illustrative only · always confirm prices with the airline/hotel
          </div>
        </main>
      </div>
    </div>
  );
}
